import Papa from "papaparse";
import type { ImportPreview, ColumnMapping } from "../types/import";

export class ClientCsvPreview {
  private static readonly PREVIEW_LINES = 50;

  async previewFile(file: File): Promise<ImportPreview> {
    // Read first 50 lines from file
    const firstLines = await this.readFirstLines(
      file,
      ClientCsvPreview.PREVIEW_LINES
    );

    // Parse with Papa Parse to detect format
    const parseResult = Papa.parse(firstLines, {
      skipEmptyLines: true,
      header: false,
    });

    if (parseResult.errors.length > 0) {
      throw new Error(`CSV parsing error: ${parseResult.errors[0].message}`);
    }

    const rows = parseResult.data as string[][];
    if (rows.length === 0) {
      throw new Error("No data found in CSV file");
    }

    // Detect format
    const detectedFormat = this.detectFormat(firstLines, rows);

    // Re-parse with detected delimiter
    const finalParseResult = Papa.parse(firstLines, {
      skipEmptyLines: true,
      header: false,
      delimiter: detectedFormat.delimiter,
    });

    const finalRows = finalParseResult.data as string[][];

    // Separate headers and data rows
    const { headers, dataRows } = this.separateHeadersAndData(
      finalRows,
      detectedFormat.has_header
    );

    // Generate suggested mappings
    const suggestedMappings = this.generateColumnMappings(headers);

    // Calculate total rows (estimate based on file size and average row length)
    const totalRows = this.estimateTotalRows(file, firstLines, dataRows.length);

    return {
      headers,
      sample_rows: dataRows.slice(0, 10), // Show first 10 data rows
      total_rows: totalRows,
      detected_format: detectedFormat,
      suggested_mappings: suggestedMappings,
    };
  }

  private async readFirstLines(file: File, lineCount: number): Promise<string> {
    const reader = file.stream().getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    let lines: string[] = [];

    try {
      while (lines.length < lineCount) {
        const { done, value } = await reader.read();

        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // Extract complete lines from buffer
        const newlineIndex = buffer.lastIndexOf("\n");
        if (newlineIndex !== -1) {
          const completeText = buffer.substring(0, newlineIndex);
          const newLines = completeText.split("\n");
          lines.push(...newLines);
          buffer = buffer.substring(newlineIndex + 1);
        }

        // Stop if we have enough lines
        if (lines.length >= lineCount) {
          lines = lines.slice(0, lineCount);
          break;
        }
      }

      // Handle remaining buffer if file ended
      if (buffer.trim() && lines.length < lineCount) {
        lines.push(buffer.trim());
      }
    } finally {
      reader.releaseLock();
    }

    return lines.join("\n");
  }

  private detectFormat(
    text: string,
    rows: string[][]
  ): {
    delimiter: "," | ";" | "\t" | "|";
    encoding: "utf8" | "latin1" | "ascii";
    skip_rows: number;
    has_header: boolean;
  } {
    // Detect delimiter by counting occurrences
    const delimiters = [",", ";", "\t", "|"] as const;
    let bestDelimiter: (typeof delimiters)[number] = ",";
    let maxCount = 0;

    for (const delimiter of delimiters) {
      const count = (text.match(new RegExp(`\\${delimiter}`, "g")) || [])
        .length;
      if (count > maxCount) {
        maxCount = count;
        bestDelimiter = delimiter;
      }
    }

    // Detect if first row is header by comparing data types
    const hasHeader = this.detectHeader(rows);

    return {
      delimiter: bestDelimiter,
      encoding: "utf8", // Default to UTF-8 for client-side
      skip_rows: 0,
      has_header: hasHeader,
    };
  }

  private detectHeader(rows: string[][]): boolean {
    if (rows.length < 2) return false;

    const firstRow = rows[0];
    const secondRow = rows[1];

    if (firstRow.length !== secondRow.length) return false;

    // Check if first row has more text content compared to second row
    let firstRowNumeric = 0;
    let secondRowNumeric = 0;

    for (let i = 0; i < Math.min(firstRow.length, secondRow.length); i++) {
      if (!isNaN(Number(firstRow[i])) && firstRow[i].trim() !== "") {
        firstRowNumeric++;
      }
      if (!isNaN(Number(secondRow[i])) && secondRow[i].trim() !== "") {
        secondRowNumeric++;
      }
    }

    // If first row has significantly fewer numeric values, likely a header
    return firstRowNumeric < secondRowNumeric * 0.7;
  }

  private separateHeadersAndData(
    rows: string[][],
    hasHeader: boolean
  ): {
    headers: string[];
    dataRows: string[][];
  } {
    if (hasHeader && rows.length > 0) {
      return {
        headers: rows[0],
        dataRows: rows.slice(1),
      };
    } else {
      // Generate column names
      const columnCount = rows[0]?.length || 0;
      const headers = Array.from(
        { length: columnCount },
        (_, i) => `Column ${i + 1}`
      );
      return {
        headers,
        dataRows: rows,
      };
    }
  }

  private generateColumnMappings(headers: string[]): ColumnMapping[] {
    return headers.map((header) => {
      const lowerHeader = header.toLowerCase();
      let targetField = "ignore";
      let required = false;

      // Match field patterns based on header names
      if (
        lowerHeader.includes("bike") ||
        lowerHeader.includes("device") ||
        lowerHeader.includes("id") ||
        lowerHeader.includes("serial")
      ) {
        targetField = "bike_id";
        required = true;
      } else if (lowerHeader.includes("lat") && !lowerHeader.includes("long")) {
        targetField = "latitude";
        required = true;
      } else if (lowerHeader.includes("lon") || lowerHeader.includes("lng")) {
        targetField = "longitude";
        required = true;
      } else if (lowerHeader.includes("time") || lowerHeader.includes("date")) {
        targetField = "timestamp";
        required = true;
      } else if (
        lowerHeader.includes("speed") ||
        lowerHeader.includes("velocity")
      ) {
        targetField = "speed";
        required = false;
      } else if (
        lowerHeader.includes("battery") ||
        lowerHeader.includes("power") ||
        lowerHeader.includes("charge") ||
        lowerHeader.includes("soc")
      ) {
        targetField = "battery_level";
        required = false;
      }

      return {
        csv_column: header,
        target_field: targetField,
        data_type: this.inferDataType(targetField),
        required,
      };
    });
  }

  private inferDataType(
    header: string
  ): "string" | "number" | "datetime" | "boolean" {
    const lowerHeader = header.toLowerCase();

    if (lowerHeader.includes("time") || lowerHeader.includes("date")) {
      return "datetime";
    } else if (
      lowerHeader.includes("lat") ||
      lowerHeader.includes("lon") ||
      lowerHeader.includes("speed") ||
      lowerHeader.includes("battery")
    ) {
      return "number";
    } else {
      return "string";
    }
  }

  private estimateTotalRows(
    file: File,
    sampleText: string,
    sampleDataRows: number
  ): number {
    if (sampleDataRows === 0) return 0;

    const sampleSize = new Blob([sampleText]).size;
    const avgBytesPerRow = sampleSize / (sampleDataRows + 1); // +1 for potential header

    // Estimate total rows based on file size
    const estimatedRows = Math.floor(file.size / avgBytesPerRow) - 1; // -1 for potential header

    return Math.max(0, estimatedRows);
  }
}

export const clientCsvPreview = new ClientCsvPreview();
